import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useProfile } from '../hooks/useProfile'
import { useMembers } from '../hooks/useMembers'
import { useSubscription } from '../hooks/useSubscription'
import { useStorageUsage } from '../features/documents/hooks/useDocuments'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Spinner } from '../components/ui/Spinner'
import { Modal } from '../components/Modal'
import { ColorPicker } from '../components/ui/ColorPicker'
import { memberColors } from '../styles/tokens'
import { getInitials, cn } from '../lib/utils'

// ── Section wrapper ─────────────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider px-4 mb-2">
      {title}
    </h2>
    <div className="bg-surface rounded-base shadow-card overflow-hidden">
      {children}
    </div>
  </div>
)

const Row = ({ label, value, onClick, chevron = true, danger = false }) => (
  <button
    onClick={onClick}
    className={cn(
      'w-full flex items-center justify-between px-4 py-3.5 text-sm',
      'border-b border-border last:border-0 hover:bg-background transition-colors text-left',
      danger ? 'text-rose-600' : 'text-text-primary'
    )}
  >
    <span className="font-medium">{label}</span>
    <div className="flex items-center gap-2 text-text-muted">
      {value && <span className="text-text-secondary text-xs">{value}</span>}
      {chevron && (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      )}
    </div>
  </button>
)

const AVATAR_SIZE_CLASSES = { 9: 'w-9 h-9', 10: 'w-10 h-10', 12: 'w-12 h-12' }

// ── Avatar component ────────────────────────────────────────────────────────
const MemberAvatar = ({ name, color, size = 9 }) => (
  <div
    className={cn(AVATAR_SIZE_CLASSES[size] ?? 'w-9 h-9', 'rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0')}
    style={{ backgroundColor: memberColors[color ?? 'violet'] }}
  >
    {getInitials(name ?? '')}
  </div>
)

// ── Role badge ───────────────────────────────────────────────────────────────
const RoleBadge = ({ role }) => {
  const styles = {
    admin:    'bg-primary-light text-primary',
    standard: 'bg-background text-text-secondary',
    limited:  'bg-amber-100 text-amber-700',
  }
  return (
    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full capitalize', styles[role] ?? styles.standard)}>
      {role}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
const formatBytes = (b) => {
  if (!b) return '0 B'
  if (b < 1048576)    return `${(b / 1024).toFixed(1)} KB`
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`
  return `${(b / 1073741824).toFixed(2)} GB`
}

export const SettingsPage = () => {
  const { user, household, isAdmin, signOut, refreshHouseholds } = useAuth()
  const { data: profile, update: updateProfile }                 = useProfile()
  const { data: members = [], invite, changeRole, remove }       = useMembers()
  const { data: sub }                                            = useSubscription()
  const { data: storageData }                                    = useStorageUsage()
  const [params]                                                 = useSearchParams()

  const isPaid = sub?.plan === 'paid' && ['active', 'trialing', 'lifetime'].includes(sub.status ?? '')
    || sub?.is_comped === true
  const storageLimit   = isPaid ? 5368709120 : 104857600
  const storageUsed    = storageData?.total_bytes ?? 0
  const storagePercent = Math.min(100, (storageUsed / storageLimit) * 100)

  // ── Panel states ────────────────────────────────────────────────────────
  const [panel, setPanel]  = useState(params.get('tab') ?? null)
  const openPanel          = (name) => setPanel(name)
  const closePanel         = () => setPanel(null)

  // Profile edit
  const [displayName, setDisplayName] = useState('')
  const [color,       setColor]       = useState('violet')
  const [avatarUrl,   setAvatarUrl]   = useState('')
  const [saving,      setSaving]      = useState(false)
  const [saveMsg,     setSaveMsg]     = useState('')

  const openProfilePanel = () => {
    setDisplayName(profile?.display_name ?? '')
    setColor(profile?.member_color ?? 'violet')
    setAvatarUrl(profile?.avatar_url ?? '')
    setSaveMsg('')
    openPanel('profile')
  }

  const handleSaveProfile = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSaveMsg('')
    try {
      await updateProfile.mutateAsync({
        display_name: displayName.trim(),
        member_color: color,
        avatar_url:   avatarUrl.trim() || null,
      })
      setSaveMsg('Saved!')
    } catch {
      setSaveMsg('Could not save — please try again.')
    }
    setSaving(false)
  }

  // Household rename
  const [hhName,    setHhName]    = useState('')
  const [hhSaving,  setHhSaving]  = useState(false)
  const [hhMsg,     setHhMsg]     = useState('')

  const openHouseholdPanel = () => {
    setHhName(household?.name ?? '')
    setHhMsg('')
    openPanel('household')
  }

  const handleRenameHousehold = async (e) => {
    e.preventDefault()
    if (!hhName.trim()) return
    setHhSaving(true)
    setHhMsg('')
    const { error } = await supabase
      .from('households')
      .update({ name: hhName.trim() })
      .eq('id', household.id)
    setHhSaving(false)
    if (error) { setHhMsg('Could not rename — please try again.'); return }
    await refreshHouseholds()
    setHhMsg('Renamed!')
  }

  // Delete household
  const [confirmDeleteHH, setConfirmDeleteHH] = useState(false)
  const [deletingHH,      setDeletingHH]      = useState(false)

  const handleDeleteHousehold = async () => {
    setDeletingHH(true)
    const { error } = await supabase.from('households').delete().eq('id', household.id)
    if (error) { setDeletingHH(false); return }
    await refreshHouseholds()
    setConfirmDeleteHH(false)
    setPanel(null)
  }

  // Invite member
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole,  setInviteRole]  = useState('standard')
  const [inviteLink,  setInviteLink]  = useState('')
  const [inviting,    setInviting]    = useState(false)
  const [inviteErr,   setInviteErr]   = useState('')

  const handleInvite = async (e) => {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    setInviting(true)
    setInviteErr('')
    try {
      const result = await invite.mutateAsync({ email: inviteEmail.trim(), role: inviteRole })
      const link = `${window.location.origin}/invite/${result.token}`
      setInviteLink(link)
      setInviteEmail('')
    } catch {
      setInviteErr('Could not create invite. Please try again.')
    }
    setInviting(false)
  }

  // Password change
  const [oldPw,    setOldPw]   = useState('')
  const [newPw,    setNewPw]   = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwMsg,    setPwMsg]   = useState('')

  const handleChangePassword = async (e) => {
    e.preventDefault()
    if (newPw.length < 8) { setPwMsg('New password must be at least 8 characters.'); return }
    setPwSaving(true)
    setPwMsg('')
    const { error } = await supabase.auth.updateUser({ password: newPw })
    setPwSaving(false)
    if (error) { setPwMsg(error.message); return }
    setPwMsg('Password updated successfully!')
    setOldPw('')
    setNewPw('')
  }

  // Delete account
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false)
  const [deletingAccount,      setDeletingAccount]      = useState(false)

  const handleDeleteAccount = async () => {
    setDeletingAccount(true)
    await supabase.rpc('delete_own_account')
    signOut()
  }

  return (
    <div className="max-w-lg mx-auto py-6 px-4">
      <h1 className="text-xl font-bold text-text-primary mb-6">Settings</h1>

      {/* ── Profile section ──────────────────────────────────────────────── */}
      <Section title="Profile">
        <div className="px-4 py-4 flex items-center gap-4 border-b border-border">
          <MemberAvatar name={profile?.display_name} color={profile?.member_color} size={12} />
          <div className="min-w-0">
            <p className="font-semibold text-text-primary truncate">{profile?.display_name ?? 'You'}</p>
            <p className="text-xs text-text-muted">{user?.email}</p>
          </div>
        </div>
        <Row label="Edit profile" value={profile?.display_name} onClick={openProfilePanel} />
      </Section>

      {/* ── Members section ──────────────────────────────────────────────── */}
      <Section title={`Members — ${household?.name ?? ''}`}>
        {members.map((m) => (
          <div key={m.user_id} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0">
            <div className="flex items-center gap-3">
              <MemberAvatar name={m.profiles?.display_name} color={m.profiles?.member_color} />
              <div>
                <p className="text-sm font-medium text-text-primary">{m.profiles?.display_name ?? 'Unknown'}</p>
                <RoleBadge role={m.role} />
              </div>
            </div>
            {isAdmin && m.user_id !== user?.id && (
              <button
                onClick={() => openPanel(`member-${m.user_id}`)}
                className="text-xs text-text-muted hover:text-primary transition-colors"
              >
                Manage
              </button>
            )}
            {/* Per-member manage modal */}
            <Modal
              open={panel === `member-${m.user_id}`}
              onClose={closePanel}
              title={`Manage ${m.profiles?.display_name ?? 'member'}`}
              footer={
                <Button variant="ghost" size="sm" onClick={closePanel}>Done</Button>
              }
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Change role</label>
                  <div className="flex gap-2">
                    {['admin','standard','limited'].map((r) => (
                      <button
                        key={r}
                        onClick={() => changeRole.mutate({ userId: m.user_id, role: r })}
                        className={cn(
                          'flex-1 py-2 rounded-base text-xs font-medium capitalize border transition-colors',
                          m.role === r
                            ? 'bg-primary text-white border-primary'
                            : 'bg-surface text-text-secondary border-border hover:border-primary'
                        )}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  className="w-full"
                  onClick={() => remove.mutate(m.user_id)}
                >
                  Remove from household
                </Button>
              </div>
            </Modal>
          </div>
        ))}
        {isAdmin && (
          <Row label="Invite new member" onClick={() => { setInviteLink(''); openPanel('invite') }} />
        )}
      </Section>

      {/* ── Household section ────────────────────────────────────────────── */}
      {isAdmin && (
        <Section title="Household">
          <Row label="Rename household" value={household?.name} onClick={openHouseholdPanel} />
          <Link to="/settings/household"
            className="flex items-center justify-between px-4 py-3.5 text-sm font-medium text-text-primary hover:bg-background transition-colors border-b border-border">
            <span>Linked households</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </Link>
          {/* Storage usage */}
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-text-primary">Storage</span>
              {!isPaid && storagePercent >= 80 && (
                <Link to="/settings/billing" className="text-xs text-amber-600 font-medium hover:underline">Upgrade for 5 GB</Link>
              )}
            </div>
            <div className="flex items-center justify-between text-xs text-text-muted mb-1.5">
              <span>{formatBytes(storageUsed)} used</span>
              <span>of {isPaid ? '5 GB' : '100 MB'}</span>
            </div>
            <div className="h-1.5 bg-border rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all',
                  storagePercent > 90 ? 'bg-red-500' : storagePercent > 70 ? 'bg-amber-400' : 'bg-emerald-500')}
                style={{ width: `${storagePercent}%` }}
              />
            </div>
          </div>
          <Row label="Delete household" danger onClick={() => setConfirmDeleteHH(true)} chevron={false} />
        </Section>
      )}

      {/* ── Billing section ──────────────────────────────────────────────── */}
      <Section title="Billing">
        <Link to="/settings/billing"
          className="flex items-center justify-between px-4 py-3.5 text-sm font-medium text-text-primary hover:bg-background transition-colors">
          <span>Subscription &amp; billing</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </Link>
      </Section>

      {/* ── Notifications section ────────────────────────────────────────── */}
      <Section title="Notifications &amp; Data">
        <Link to="/settings/notifications"
          className="flex items-center justify-between px-4 py-3.5 text-sm font-medium text-text-primary hover:bg-background transition-colors border-b border-border">
          <span>Email notifications</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </Link>
        <Link to="/settings/data-export"
          className="flex items-center justify-between px-4 py-3.5 text-sm font-medium text-text-primary hover:bg-background transition-colors">
          <span>Export my data</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </Link>
      </Section>

      {/* ── Security section ─────────────────────────────────────────────── */}
      <Section title="Security">
        <Link to="/settings/security"
          className="flex items-center justify-between px-4 py-3.5 text-sm font-medium text-text-primary hover:bg-background transition-colors border-b border-border">
          <span>Two-factor authentication</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </Link>
        <Row label="Change password"  onClick={() => openPanel('password')} />
      </Section>

      {/* ── Account section ──────────────────────────────────────────────── */}
      <Section title="Account">
        <Row label="Sign out"         onClick={signOut} chevron={false} />
        <Row label="Delete account"   danger onClick={() => setConfirmDeleteAccount(true)} chevron={false} />
      </Section>

      {/* ════════════════════════════════════════════════════════════════════
          MODALS
          ═══════════════════════════════════════════════════════════════════ */}

      {/* Edit profile */}
      <Modal open={panel === 'profile'} onClose={closePanel} title="Edit profile"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closePanel}>Cancel</Button>
            <Button size="sm" onClick={handleSaveProfile} disabled={saving}>
              {saving ? <Spinner size="sm" /> : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSaveProfile} className="space-y-5">
          <Input label="Display name" type="text" value={displayName}
            onChange={(e) => setDisplayName(e.target.value)} />
          <Input label="Avatar URL" type="url" value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value)}
            hint="Link to a profile image (optional)" placeholder="https://..." />
          <ColorPicker label="Member color" value={color} onChange={setColor} />
          {saveMsg && (
            <p className={cn('text-sm', saveMsg.includes('Could not') ? 'text-rose-600' : 'text-emerald-600')}>
              {saveMsg}
            </p>
          )}
        </form>
      </Modal>

      {/* Rename household */}
      <Modal open={panel === 'household'} onClose={closePanel} title="Rename household"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closePanel}>Cancel</Button>
            <Button size="sm" onClick={handleRenameHousehold} disabled={hhSaving}>
              {hhSaving ? <Spinner size="sm" /> : 'Rename'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Household name" type="text" value={hhName}
            onChange={(e) => setHhName(e.target.value)} autoFocus />
          {hhMsg && (
            <p className={cn('text-sm', hhMsg.includes('Could not') ? 'text-rose-600' : 'text-emerald-600')}>
              {hhMsg}
            </p>
          )}
        </div>
      </Modal>

      {/* Invite member */}
      <Modal open={panel === 'invite'} onClose={closePanel} title="Invite a member">
        <div className="space-y-5">
          {inviteLink ? (
            <>
              <p className="text-sm text-text-secondary">Share this link:</p>
              <div className="flex gap-2">
                <input readOnly value={inviteLink}
                  className="flex-1 rounded-base border border-border bg-background px-3 py-2 text-xs text-text-muted focus:outline-none" />
                <Button variant="secondary" size="sm"
                  onClick={async () => { try { await navigator.clipboard.writeText(inviteLink) } catch { /* ignore */ } }}>
                  Copy
                </Button>
              </div>
              <Button className="w-full" onClick={() => { setInviteLink(''); closePanel() }}>Done</Button>
            </>
          ) : (
            <form onSubmit={handleInvite} className="space-y-4">
              <Input label="Email address" type="email" placeholder="family@example.com"
                value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
              <div className="space-y-1">
                <label className="block text-sm font-medium text-text-primary">Role</label>
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full rounded-base border border-border bg-surface px-4 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="admin">Admin</option>
                  <option value="standard">Standard</option>
                  <option value="limited">Limited</option>
                </select>
              </div>
              {inviteErr && <p className="text-sm text-rose-600">{inviteErr}</p>}
              <Button type="submit" className="w-full" disabled={inviting}>
                {inviting ? <><Spinner size="sm" /> Creating…</> : 'Create invite link'}
              </Button>
            </form>
          )}
        </div>
      </Modal>

      {/* Change password */}
      <Modal open={panel === 'password'} onClose={closePanel} title="Change password"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closePanel}>Cancel</Button>
            <Button size="sm" onClick={handleChangePassword} disabled={pwSaving}>
              {pwSaving ? <Spinner size="sm" /> : 'Update'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleChangePassword} className="space-y-4">
          <Input label="New password" type="password" placeholder="At least 8 characters"
            value={newPw} onChange={(e) => setNewPw(e.target.value)}
            autoComplete="new-password" />
          {pwMsg && (
            <p className={cn('text-sm', pwMsg.includes('successfully') ? 'text-emerald-600' : 'text-rose-600')}>
              {pwMsg}
            </p>
          )}
        </form>
      </Modal>

      {/* Confirm delete household */}
      <Modal open={confirmDeleteHH} onClose={() => setConfirmDeleteHH(false)}
        title="Delete household?"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteHH(false)}>Cancel</Button>
            <Button variant="danger" size="sm" onClick={handleDeleteHousehold} disabled={deletingHH}>
              {deletingHH ? <Spinner size="sm" /> : 'Delete'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          This will permanently delete <strong>{household?.name}</strong> and all its data — including
          events, tasks, shopping lists, and budgets. This action cannot be undone.
        </p>
      </Modal>

      {/* Confirm delete account */}
      <Modal open={confirmDeleteAccount} onClose={() => setConfirmDeleteAccount(false)}
        title="Delete your account?"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteAccount(false)}>Cancel</Button>
            <Button variant="danger" size="sm" onClick={handleDeleteAccount} disabled={deletingAccount}>
              {deletingAccount ? <Spinner size="sm" /> : 'Delete account'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          Your account and all personal data will be permanently deleted. You will be removed from
          all households. This cannot be undone.
        </p>
      </Modal>
    </div>
  )
}
